import { cva, type VariantProps } from 'class-variance-authority';
import { Popover as PopoverPrimitive } from 'radix-ui';
import {
  useId,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';

import { cn } from '@/lib/cn';

/**
 * Popover — the anchored, non-modal panel (`FE-04.10`).
 *
 * Asked for as **New** by two of the three Stage 2 returns independently:
 * `card-back.md` (the member picker, the label picker and the label editor
 * inside it) and `finding-things.md` (the board filter panel and the
 * notification drawer).
 *
 * ── It is a checkbox group. It is not a Menu and it is not a Dialog ────────
 *
 * B3-BRIEF §5.1 settles the one genuine fork in the chunk, and both wrong
 * answers are attractive:
 *
 * - **Not a `Menu`.** Radix menu children are `role="menuitem"`, which may not
 *   contain a text input — and every surface that uses this opens with a search
 *   box (`FR-CARD-008` AC1, `FR-CARD-010`). A menu also closes on the first
 *   activation, while these are multi-select toggles that must stay open
 *   (`FR-CARD-009` AC1, `FR-CARD-008` AC3). Build these as menus and "the ARIA
 *   is wrong on all three and nothing reports it until B41."
 * - **Not a `Dialog`.** A dialog traps focus and scrims. `FR-SEARCH-001` AC2
 *   requires clicking outside to close the panel *with criteria still active*,
 *   which a modal cannot express.
 * - **Not a listbox.** `finding-things.md`'s `variant: 'listbox'` is dropped
 *   here: that focus model — `aria-activedescendant`, focus never leaving the
 *   input — lives in `Combobox` and nowhere else. Option rows inside this panel
 *   are a plain checkbox group: **each row is its own tab stop, there is no
 *   roving `tabindex`, and nothing in this panel carries
 *   `aria-activedescendant`.** Roving `tabindex` belongs to radios, listboxes
 *   and toolbars; individual checkboxes each take a tab stop, and every list
 *   these pickers show is bounded.
 *
 * Rows are the caller's: a native `<input type="checkbox">` inside a `<label>`
 * (implicit `role="checkbox"`, one tab stop, the name from the label), or
 * `FacetOption`, which `finding-things.md` specifies and which is not a B3 file.
 * This primitive owns the panel and its dismissal contract, not the row.
 *
 * ── Colour ────────────────────────────────────────────────────────────────
 * Opaque `--color-surface-raised`, never `--color-panel`: all three surfaces
 * are full of muted secondary text and the panel token's measured floor is 92%
 * where `--color-ink-muted` needs 98% (`finding-things.md` Decision 1). The 1px
 * edge is `--color-border-strong`, not `--color-border`, because it delineates a
 * control surface.
 */

const popoverPanelVariants = cva(
  'z-50 flex max-h-[min(var(--radix-popover-content-available-height,80vh),35rem)] flex-col overflow-hidden rounded-card border border-border-strong bg-surface-raised text-ink shadow-card',
  {
    variants: {
      variant: {
        /**
         * The picker / filter panel. 304px is `card-back.md`'s explicit width;
         * `finding-things.md` draws its filter panel at 320px. Neither is
         * binding and the difference is why `width` exists — see the chunk
         * return.
         */
        panel: 'w-[19rem]',
        /**
         * The notification drawer: wider, taller, because its rows carry an
         * avatar, a sentence and two controls.
         */
        drawer:
          'h-[min(var(--radix-popover-content-available-height,80vh),40rem)] w-[24rem] max-h-[min(var(--radix-popover-content-available-height,80vh),40rem)]',
      },
    },
    defaultVariants: { variant: 'panel' },
  },
);

export interface PopoverProps
  extends
    VariantProps<typeof popoverPanelVariants>,
    Omit<ComponentPropsWithoutRef<'div'>, 'title'> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The anchor. Rendered through Radix's `asChild`, so it must be a single
   * element that accepts a ref — the trigger receives `aria-expanded`,
   * `aria-controls` and `aria-haspopup` from the primitive.
   */
  trigger: ReactNode;
  /** Rendered in the header and used as the panel's accessible name. */
  title: ReactNode;
  /**
   * The close control, as a slot (B3-BRIEF §9). The caller passes
   * `<IconButton label="Close" variant="quiet" size="sm" />`; the primitive
   * wraps it in Radix's `Popover.Close` so the caller wires no state.
   */
  closeControl?: ReactNode;
  /** The search box slot, directly under the title. Sticky with the header. */
  search?: ReactNode;
  /** Sticky footer — "Clear all", "Create a new label". */
  footer?: ReactNode;
  /**
   * `bottom-start` (default) or `bottom-end`. Only the alignment varies: every
   * one of these panels hangs below its anchor.
   */
  placement?: 'bottom-start' | 'bottom-end';
  /** Overrides the variant's width. A number is read as pixels. */
  width?: number | string;
  /**
   * Focused on open. `FR-SEARCH-001` AC1 puts keyboard focus in the keyword
   * input when the filter panel opens; without this Radix focuses the panel
   * container and the global focus ring draws around the whole panel.
   */
  initialFocus?: RefObject<HTMLElement | null>;
  children: ReactNode;
}

export function Popover({
  open,
  onOpenChange,
  trigger,
  title,
  closeControl,
  search,
  footer,
  placement = 'bottom-start',
  width,
  initialFocus,
  variant,
  className,
  style,
  children,
  ...rest
}: PopoverProps) {
  const titleId = useId();

  const panelStyle: CSSProperties | undefined =
    width === undefined
      ? style
      : { ...style, width: typeof width === 'number' ? `${width}px` : width };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          {...rest}
          /*
           * `modal` is deliberately not a prop. Non-modal is the whole point:
           * no scrim, no focus trap, the board behind stays scrollable and
           * clickable, Escape dismisses, an outside click dismisses **with
           * state intact**, and focus returns to the anchor.
           */
          side="bottom"
          align={placement === 'bottom-end' ? 'end' : 'start'}
          sideOffset={6}
          collisionPadding={12}
          role="dialog"
          aria-labelledby={titleId}
          style={panelStyle}
          className={cn(popoverPanelVariants({ variant }), className)}
          onOpenAutoFocus={(event) => {
            if (!initialFocus?.current) return;
            event.preventDefault();
            initialFocus.current.focus();
          }}
        >
          <div className="shrink-0 border-b border-border px-3 py-2.5">
            <div className="flex items-center gap-2">
              <h2
                id={titleId}
                className="min-w-0 flex-1 truncate text-sm font-medium text-ink"
              >
                {title}
              </h2>
              {closeControl ? (
                <PopoverPrimitive.Close asChild>{closeControl}</PopoverPrimitive.Close>
              ) : null}
            </div>
            {search ? <div className="mt-2">{search}</div> : null}
          </div>

          <div
            className={cn(
              'min-h-0 flex-1 px-3 py-2',
              variant === 'drawer' ? 'overflow-hidden' : 'overflow-y-auto',
            )}
          >
            {children}
          </div>

          {footer ? (
            <div className="shrink-0 border-t border-border px-3 py-2">{footer}</div>
          ) : null}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
